import * as React from 'react';
import Image from 'next/image';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The row that opens a page section: an optional icon, a serif title, and an
 * optional action on the far right.
 *
 * This is not a new design. It shipped as an unexported local in
 * pages/events/index.tsx and was the best of four competing section-header
 * patterns in the app, so the job here was to export it, not to redraw it —
 * the markup below is the original, class for class.
 *
 * Adoption is deliberately narrow. Only headers whose markup was already
 * equivalent were switched over; the ones that carry a subtitle, a live count,
 * a sticky offset or a non-serif title were left where they are, because
 * folding them in would mean changing how they look under cover of a
 * refactor. They are listed in the node's report as follow-up, not hidden.
 */
export interface SectionHeaderAction {
  label: string;
  onClick: () => void;
}

export interface SectionHeaderProps {
  title: string;
  /** Path to a square glyph rendered at 22px before the title. */
  icon?: string;
  action?: SectionHeaderAction;
  className?: string;
}

export function SectionHeader({ title, icon, action, className }: SectionHeaderProps) {
  return (
    <div className={cn('mb-3 flex items-center justify-between gap-3', className)}>
      <div className="flex items-center gap-2">
        {icon && (
          <Image
            src={icon}
            alt=""
            width={22}
            height={22}
            className="h-[22px] w-[22px] object-contain opacity-85"
          />
        )}
        {/* text-heading is the type scale's 18px step — byte-identical to the
            text-lg this shipped with, and it carries font-weight 600 so the
            heading no longer has to say font-bold to look like a heading.
            sm:text-xl stays a Tailwind step: the scale has no slot between
            heading and title, and inventing one to serve a single responsive
            bump would be the drift this node exists to remove. */}
        <h2 className="font-serif text-heading font-bold text-cream sm:text-xl">{title}</h2>
      </div>
      {action && (
        <button
          onClick={action.onClick}
          className="eyebrow flex shrink-0 items-center gap-1 text-xs font-bold text-gold transition-colors hover:text-gold-hover"
        >
          {action.label} <ChevronRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
